<template>
    <div class="p-2">
        <Card>
            <template #title>
                Configurações
            </template>
            <template #content>
                <h3 class="mt-0 mb-2 text-lg">Google Gemini (análise de boleto por IA) — legado</h3>
                <p class="text-sm text-gray-600 mb-4">
                    Armazenamento separado da tabela “Integrações de API” abaixo: criar ou editar uma integração
                    <strong class="font-medium">não preenche nem altera</strong> este campo legado.
                    Pode usar <code class="text-xs">GEMINI_API_KEY</code> no servidor ou guardar a chave aqui (encriptada na base).
                    Se existir valor legado na base, ele substitui o
                    <code class="text-xs">.env</code> para esse mecanismo.
                    <strong class="font-medium">Na chamada à API Gemini,</strong> a ordem de precedência é: integração global na tabela → este legado →
                    <code class="text-xs">.env</code>.
                </p>

                <div v-if="loading" class="py-4 flex justify-content-center">
                    <ProgressSpinner style="width: 42px; height: 42px" strokeWidth="4" />
                </div>

                <div v-else class="flex flex-column gap-3" style="max-width: 36rem">
                    <Message
                        v-if="status.effective_configured && status.fonte_efetiva_gemini === 'env'"
                        severity="info"
                        :closable="false"
                    >
                        O upload IA está usando apenas a variável <code class="text-xs">GEMINI_API_KEY</code> do arquivo
                        <code class="text-xs">.env</code> no servidor. Você não guardou chave legado neste formulário e não há
                        integração Gemini global ativa na tabela abaixo com precedência sobre o ambiente.
                    </Message>
                    <Message
                        v-else-if="status.effective_configured && status.fonte_efetiva_gemini === 'integracao'"
                        severity="success"
                        :closable="false"
                    >
                        Upload IA ativo: está em uso uma integração Gemini da tabela abaixo (prioridade sobre chave legado e sobre o
                        <code class="text-xs">.env</code>).
                    </Message>
                    <Message
                        v-else-if="status.effective_configured && status.fonte_efetiva_gemini === 'legado'"
                        severity="success"
                        :closable="false"
                    >
                        Upload IA ativo: chave legado guardada na base de dados (substitui a variável
                        <code class="text-xs">GEMINI_API_KEY</code> do ambiente quando aplicável).
                    </Message>
                    <Message v-else-if="status.effective_configured" severity="success" :closable="false">
                        Integração Gemini disponível para o upload IA.
                    </Message>
                    <Message v-else severity="warn" :closable="false">
                        Nenhuma chave disponível. Configure GEMINI_API_KEY, uma integração Gemini abaixo ou guarde uma chave legado aqui.
                    </Message>

                    <div class="field">
                        <label for="gemini-api-key" class="block mb-1 font-medium">Chave API (Gemini) — legado</label>
                        <Password
                            id="gemini-api-key"
                            v-model="apiKey"
                            class="w-full"
                            inputClass="w-full"
                            :feedback="false"
                            toggleMask
                            autocomplete="off"
                        />
                    </div>

                    <div class="flex flex-wrap gap-2">
                        <Button
                            label="Guardar"
                            icon="pi pi-check"
                            severity="info"
                            :loading="saving"
                            :disabled="loading"
                            @click="confirmSaveLegacy"
                        />
                        <Button
                            v-if="status.database_override"
                            label="Remover chave da base"
                            icon="pi pi-trash"
                            severity="secondary"
                            outlined
                            :loading="saving"
                            :disabled="loading"
                            @click="confirmRemoveLegacyOverride"
                        />
                    </div>
                </div>
            </template>
        </Card>

        <Card class="mt-3">
            <template #title>
                Integrações de API (IA)
            </template>
            <template #content>
                <p class="text-sm text-gray-600 mb-3">
                    Cadastre chaves nesta tabela (encriptadas na base). Isto
                    <strong class="font-medium">não altera</strong> o bloco “Gemini legado” acima — são cadastros independentes.
                    Por segurança, a chave não aparece na listagem; só ao criar ou editar.
                    O fluxo de boleto por IA usa <strong class="font-medium">Google Gemini</strong>;
                    <strong class="font-medium">OpenAI</strong> fica para extensões futuras.
                    Apenas uma integração ativa por provedor no âmbito global (sem cliente).
                </p>

                <div class="mb-3">
                    <Button
                        label="Nova integração"
                        icon="pi pi-plus"
                        severity="info"
                        :disabled="loadingIntegracoes"
                        @click="openIntegracaoForm(null)"
                    />
                </div>

                <DataTable
                    :value="integracoes"
                    :loading="loadingIntegracoes"
                    size="small"
                    stripedRows
                    showGridlines
                    responsiveLayout="scroll"
                    dataKey="id"
                >
                    <Column field="provedor" header="Provedor" sortable>
                        <template #body="{ data }">
                            {{ labelProvedor(data.provedor) }}
                        </template>
                    </Column>
                    <Column field="modelo" header="Modelo">
                        <template #body="{ data }">
                            {{ labelModeloUi(data.provedor, data.modelo) }}
                        </template>
                    </Column>
                    <Column field="ativo" header="Estado" sortable>
                        <template #body="{ data }">
                            <Tag :severity="data.ativo ? 'success' : 'secondary'" :value="data.ativo ? 'Ativa' : 'Inativa'" />
                        </template>
                    </Column>
                    <Column style="width: 8rem" bodyClass="flex justify-content-end gap-1 flex-wrap">
                        <template #body="{ data }">
                            <Button
                                icon="pi pi-pencil"
                                rounded
                                text
                                severity="info"
                                v-tooltip.top="'Editar'"
                                @click="openIntegracaoForm(data)"
                            />
                            <Button
                                icon="pi pi-trash"
                                rounded
                                text
                                severity="danger"
                                v-tooltip.top="'Excluir'"
                                @click="confirmRemoveIntegracao(data)"
                            />
                        </template>
                    </Column>
                </DataTable>
            </template>
        </Card>

        <Card class="mt-3">
            <template #title>
                Configuração de SLA do Cancelamento
            </template>
            <template #content>
                <p class="text-sm text-gray-600 mb-3">
                    Defina os prazos em horas para cada etapa/prioridade. Valor de Atenção deve ser menor que valor de Estourado.
                </p>

                <DataTable
                    v-model:editingRows="slaEditingRows"
                    :value="slaLista"
                    :loading="slaIsLoading"
                    editMode="row"
                    size="small"
                    stripedRows
                    showGridlines
                    dataKey="id"
                    @row-edit-save="onSlaRowEditSave"
                >
                    <Column field="etapa" header="Etapa">
                        <template #body="{ data }">
                            {{ etapaLabel(data.etapa) }}
                        </template>
                    </Column>
                    <Column field="prioridade" header="Prioridade">
                        <template #body="{ data }">
                            <Tag
                                :value="prioridadeLabel(data.prioridade)"
                                :severity="prioridadeSeverity(data.prioridade)"
                            />
                        </template>
                    </Column>
                    <Column field="prazo_horas_atencao" header="Horas Atenção">
                        <template #editor="{ data, field }">
                            <InputNumber v-model="data[field]" :min="1" :max="9999" showButtons />
                        </template>
                    </Column>
                    <Column field="prazo_horas_estourado" header="Horas Estourado">
                        <template #editor="{ data, field }">
                            <InputNumber v-model="data[field]" :min="1" :max="9999" showButtons />
                        </template>
                    </Column>
                    <Column :rowEditor="true" style="width: 100px" bodyClass="flex justify-content-end" />
                    <template #empty>
                        <div class="text-center text-color-secondary py-4">Nenhuma configuração de SLA encontrada.</div>
                    </template>
                </DataTable>
            </template>
        </Card>

        <div v-if="permissoes.CONFIGURACOES_DADOS_BANCARIOS" class="mt-3">
            <DadosBancariosSection />
        </div>

        <Dialog
            v-model:visible="integracaoDialogVisible"
            :header="integracaoEditId ? 'Editar integração' : 'Nova integração'"
            modal
            class="dialog-integracao-ia"
            :style="{ width: '22rem', maxWidth: 'min(22rem, calc(100vw - 2rem))' }"
            :closable="!integracaoSaving"
            @hide="resetIntegracaoForm"
        >
            <div class="flex flex-column gap-2 pt-0 dialog-integracao-ia__body">
                <div class="field">
                    <label for="ia-provedor" class="block mb-1 font-medium">Provedor<span class="text-red-500"> *</span></label>
                    <Select
                        id="ia-provedor"
                        v-model="integracaoForm.provedor"
                        :options="provedorOptions"
                        optionLabel="label"
                        optionValue="value"
                        class="w-full"
                        :disabled="integracaoSaving || !!integracaoEditId"
                        placeholder="Selecione"
                    />
                    <small v-if="integracaoEditId" class="text-gray-500">O provedor não pode ser alterado após criar.</small>
                </div>

                <div class="field">
                    <label for="ia-api-key" class="block mb-1 font-medium">
                        Chave API<span class="text-red-500"> *</span>
                    </label>
                    <Password
                        id="ia-api-key"
                        v-model="integracaoForm.api_key"
                        class="w-full"
                        inputClass="w-full"
                        :feedback="false"
                        toggleMask
                        autocomplete="off"
                        :disabled="integracaoSaving"
                    />
                    <small v-if="integracaoEditId" class="text-gray-500">Deixe em branco para manter a chave atual.</small>
                </div>

                <div class="field">
                    <label for="ia-modelo" class="block mb-1 font-medium">Modelo<span class="text-red-500"> *</span></label>
                    <Select
                        id="ia-modelo"
                        v-model="integracaoForm.modelo"
                        :options="integracaoModeloSelectOptions"
                        optionLabel="label"
                        optionValue="value"
                        class="w-full"
                        :disabled="integracaoSaving"
                        placeholder="Selecione o modelo"
                    />
                </div>

                <div class="flex align-items-center gap-2">
                    <Checkbox v-model="integracaoForm.ativo" inputId="ia-ativo" binary :disabled="integracaoSaving" />
                    <label for="ia-ativo" class="m-0 cursor-pointer">Integração ativa</label>
                </div>
            </div>

            <template #footer>
                <div class="flex justify-content-end gap-2 flex-wrap">
                    <Button label="Cancelar" severity="secondary" text :disabled="integracaoSaving" @click="integracaoDialogVisible = false" />
                    <Button label="Guardar" severity="info" icon="pi pi-check" :loading="integracaoSaving" @click="submitIntegracaoForm" />
                </div>
            </template>
        </Dialog>

    </div>
</template>

<script>
import Card from 'primevue/card';
import Button from 'primevue/button';
import Password from 'primevue/password';
import Message from 'primevue/message';
import ProgressSpinner from 'primevue/progressspinner';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Dialog from 'primevue/dialog';
import Select from 'primevue/select';
import Checkbox from 'primevue/checkbox';
import Tag from 'primevue/tag';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';

import ConfiguracaoSistema from '@/service/configuracaoSistema.js';
import Auth from '@/service/auth.js';
import slaConfigService from '@/service/cancelamento-sla-config';
import DadosBancariosSection from './DadosBancariosSection.vue';

const PROVEDOR_OPTIONS = [
    { label: 'Google Gemini', value: 'gemini' },
    { label: 'OpenAI', value: 'openai' },
];

const ETAPA_LABELS = {
    canc_aguardando: 'Aguardando assumir',
    canc_em_tratativa: 'Aguardando cancelamento',
    canc_aguardando_retirada: 'Aguardando retirada',
    canc_aguardando_pagamento: 'Aguardando pagamento',
    canc_aguardando_gestao: 'Aguardando gestão',
    canc_aguardando_ok: 'Aguardando finalização',
};

const PRIORIDADE_LABELS = {
    alta: 'Alta',
    media: 'Média',
    baixa: 'Baixa',
};

const PRIORIDADE_SEVERITY = {
    alta: 'danger',
    media: 'warn',
    baixa: 'secondary',
};

/** IDs alinhados a {@see App\Models\IaIntegracao::MODELOS_GEMINI_API} / MODELOS_OPENAI_API */
const IA_MODEL_OPTIONS_GEMINI = [
    { label: 'Gemini 2.5 Flash (recomendado)', value: 'gemini-2.5-flash' },
    { label: 'Gemini 2.5 Pro', value: 'gemini-2.5-pro' },
    { label: 'Gemini 2.5 Flash-Lite', value: 'gemini-2.5-flash-lite' },
    { label: 'Gemini 3 Flash (preview)', value: 'gemini-3-flash-preview' },
    { label: 'Gemini 3.1 Pro (preview)', value: 'gemini-3.1-pro-preview' },
    { label: 'Gemini 3.1 Flash-Lite (preview)', value: 'gemini-3.1-flash-lite-preview' },
];

const IA_MODEL_OPTIONS_OPENAI = [
    { label: 'GPT-4o', value: 'gpt-4o' },
    { label: 'GPT-4o mini', value: 'gpt-4o-mini' },
    { label: 'GPT-4 Turbo', value: 'gpt-4-turbo' },
    { label: 'o4-mini', value: 'o4-mini' },
    { label: 'o3-mini', value: 'o3-mini' },
];

function defaultModeloParaProvedor(provedor) {
    const list = provedor === 'openai' ? IA_MODEL_OPTIONS_OPENAI : IA_MODEL_OPTIONS_GEMINI;
    return list[0]?.value ?? 'gemini-2.5-flash';
}

export default {
    name: 'ConfiguracaoSistemaPage',
    components: {
        Card,
        Button,
        Password,
        Message,
        ProgressSpinner,
        DataTable,
        Column,
        Dialog,
        Select,
        Checkbox,
        Tag,
        InputText,
        InputNumber,
        DadosBancariosSection,
    },
    data() {
        return {
            loading: true,
            saving: false,
            apiKey: '',
            status: {
                database_override: false,
                environment_configured: false,
                effective_configured: false,
                fonte_efetiva_gemini: null,
            },
            integracoes: [],
            loadingIntegracoes: true,
            integracaoDialogVisible: false,
            integracaoSaving: false,
            integracaoEditId: null,
            integracaoForm: {
                provedor: 'gemini',
                api_key: '',
                modelo: defaultModeloParaProvedor('gemini'),
                ativo: true,
            },
            provedorOptions: PROVEDOR_OPTIONS,
            slaLista: [],
            slaIsLoading: false,
            slaEditingRows: [],
            permissoes: typeof permissoes !== 'undefined' ? permissoes : {},
        };
    },
    computed: {
        integracaoModeloSelectOptions() {
            const base =
                this.integracaoForm.provedor === 'openai' ? IA_MODEL_OPTIONS_OPENAI : IA_MODEL_OPTIONS_GEMINI;
            const m = (this.integracaoForm.modelo || '').trim();
            if (m && !base.some((o) => o.value === m)) {
                return [{ label: `${m} (atual na base)`, value: m }, ...base];
            }
            return base;
        },
    },
    watch: {
        'integracaoForm.provedor'(newVal, oldVal) {
            if (this.integracaoEditId) {
                return;
            }
            if (oldVal === undefined || newVal === oldVal) {
                return;
            }
            this.integracaoForm.modelo = defaultModeloParaProvedor(newVal);
        },
    },
    mounted() {
        document.addEventListener('onPermissios', this.onPermissoesAtualizadas);
        this.loadAll();
    },
    beforeUnmount() {
        document.removeEventListener('onPermissios', this.onPermissoesAtualizadas);
    },
    methods: {
        onPermissoesAtualizadas(event) {
            this.permissoes = event.permissoes ?? {};
        },
        syncPermissoesGlobais() {
            if (typeof permissoes !== 'undefined' && Object.keys(permissoes).length > 0) {
                this.permissoes = permissoes;
            }
        },
        async ensurePermissoesCarregadas() {
            this.syncPermissoesGlobais();

            if (Object.keys(this.permissoes).length > 0) {
                return;
            }

            const { data } = await Auth.permissoes();
            this.permissoes = data ?? {};
            permissoes = this.permissoes;

            const event = new Event('onPermissios');
            event.permissoes = this.permissoes;
            document.dispatchEvent(event);
        },
        async loadAll() {
            try {
                await this.ensurePermissoesCarregadas();
            } catch (e) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: e.response?.data?.msg || e.message || 'Não foi possível carregar as permissões.',
                    life: 5000,
                });
                return;
            }

            await Promise.all([
                this.loadStatus(),
                this.loadIntegracoes(),
            ]);

            await this.loadSlaConfig();
        },
        etapaLabel(etapa) {
            return ETAPA_LABELS[etapa] ?? etapa;
        },
        prioridadeLabel(prioridade) {
            return PRIORIDADE_LABELS[prioridade] ?? prioridade;
        },
        prioridadeSeverity(prioridade) {
            return PRIORIDADE_SEVERITY[prioridade] ?? 'info';
        },
        loadSlaConfig() {
            this.slaIsLoading = true;
            return slaConfigService.list()
                .then((r) => {
                    this.slaLista = r.data?.data ?? r.data ?? [];
                })
                .catch((error) => {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: slaConfigService.extractErrorMessage(error),
                        life: 5000,
                    });
                })
                .finally(() => {
                    this.slaIsLoading = false;
                });
        },
        onSlaRowEditSave(event) {
            const { newData, index } = event;
            const prazoAtencao = Number(newData.prazo_horas_atencao);
            const prazoEstourado = Number(newData.prazo_horas_estourado);

            if (!Number.isFinite(prazoAtencao) || !Number.isFinite(prazoEstourado)) {
                this.$toast.add({
                    severity: 'warn',
                    summary: 'Valores inválidos',
                    detail: 'Informe valores numéricos para os prazos.',
                    life: 5000,
                });
                return;
            }

            if (prazoAtencao >= prazoEstourado) {
                this.$toast.add({
                    severity: 'warn',
                    summary: 'Prazos inválidos',
                    detail: 'O prazo de atenção deve ser menor que o prazo de estourado.',
                    life: 5000,
                });
                return;
            }

            slaConfigService.update(newData.id, {
                prazo_horas_atencao: prazoAtencao,
                prazo_horas_estourado: prazoEstourado,
            })
                .then((r) => {
                    const atualizado = r.data?.data ?? newData;
                    if (typeof index === 'number' && this.slaLista[index]) {
                        this.slaLista.splice(index, 1, { ...this.slaLista[index], ...atualizado });
                    }
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Sucesso',
                        detail: 'SLA atualizado com sucesso',
                        life: 5000,
                    });
                })
                .catch((error) => {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: slaConfigService.extractErrorMessage(error),
                        life: 5000,
                    });
                });
        },
        labelProvedor(value) {
            const o = PROVEDOR_OPTIONS.find((x) => x.value === value);
            return o ? o.label : value;
        },
        labelModeloUi(provedor, value) {
            if (!value) {
                return '—';
            }
            const list = provedor === 'openai' ? IA_MODEL_OPTIONS_OPENAI : IA_MODEL_OPTIONS_GEMINI;
            const o = list.find((x) => x.value === value);
            return o ? o.label : value;
        },
        async loadStatus() {
            this.loading = true;
            try {
                const { data } = await ConfiguracaoSistema.geminiStatus();
                this.status = {
                    database_override: !!data.database_override,
                    environment_configured: !!data.environment_configured,
                    effective_configured: !!data.effective_configured,
                    fonte_efetiva_gemini: data.fonte_efetiva_gemini ?? null,
                };
                this.apiKey = '';
            } catch (e) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: e.response?.data?.message || e.message || 'Não foi possível carregar as configurações.',
                    life: 5000,
                });
            } finally {
                this.loading = false;
            }
        },
        async loadIntegracoes() {
            this.loadingIntegracoes = true;
            try {
                const { data } = await ConfiguracaoSistema.iaIntegracoesList();
                this.integracoes = Array.isArray(data.data) ? data.data : [];
            } catch (e) {
                this.integracoes = [];
                this.$toast.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail:
                        e.response?.data?.message ||
                        e.message ||
                        'Não foi possível carregar as integrações de IA.',
                    life: 5000,
                });
            } finally {
                this.loadingIntegracoes = false;
            }
        },
        openIntegracaoForm(row) {
            this.integracaoEditId = row?.id ?? null;
            if (row) {
                const p = row.provedor || 'gemini';
                this.integracaoForm = {
                    provedor: p,
                    api_key: '',
                    modelo: row.modelo || defaultModeloParaProvedor(p),
                    ativo: !!row.ativo,
                };
            } else {
                this.integracaoForm = {
                    provedor: 'gemini',
                    api_key: '',
                    modelo: defaultModeloParaProvedor('gemini'),
                    ativo: true,
                };
            }
            this.integracaoDialogVisible = true;
        },
        resetIntegracaoForm() {
            this.integracaoEditId = null;
            this.integracaoForm = {
                provedor: 'gemini',
                api_key: '',
                modelo: defaultModeloParaProvedor('gemini'),
                ativo: true,
            };
        },
        firstValidationMessage(errors) {
            if (!errors || typeof errors !== 'object') {
                return null;
            }
            const firstKey = Object.keys(errors)[0];
            if (!firstKey) {
                return null;
            }
            const v = errors[firstKey];
            return Array.isArray(v) ? v[0] : v;
        },
        async submitIntegracaoForm() {
            const keyTrim = (this.integracaoForm.api_key || '').trim();
            if (!this.integracaoEditId && !keyTrim) {
                this.$toast.add({
                    severity: 'warn',
                    summary: 'Atenção',
                    detail: 'Informe a chave API.',
                    life: 4000,
                });
                return;
            }
            const modeloVal = (this.integracaoForm.modelo || '').trim();
            if (!modeloVal) {
                this.$toast.add({
                    severity: 'warn',
                    summary: 'Atenção',
                    detail: 'Selecione um modelo.',
                    life: 4000,
                });
                return;
            }

            this.integracaoSaving = true;
            try {
                if (this.integracaoEditId) {
                    const payload = {
                        provedor: this.integracaoForm.provedor,
                        modelo: modeloVal,
                        ativo: !!this.integracaoForm.ativo,
                    };
                    if (keyTrim) {
                        payload.api_key = keyTrim;
                    }
                    const { data } = await ConfiguracaoSistema.iaIntegracaoUpdate(this.integracaoEditId, payload);
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Sucesso',
                        detail: data.message || 'Integração atualizada.',
                        life: 4000,
                    });
                } else {
                    const { data } = await ConfiguracaoSistema.iaIntegracaoStore({
                        provedor: this.integracaoForm.provedor,
                        api_key: keyTrim,
                        modelo: modeloVal,
                        ativo: !!this.integracaoForm.ativo,
                    });
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Sucesso',
                        detail: data.message || 'Integração criada.',
                        life: 4000,
                    });
                }
                this.integracaoDialogVisible = false;
                await Promise.all([this.loadIntegracoes(), this.loadStatus()]);
            } catch (e) {
                const err = e.response?.data;
                const msg =
                    this.firstValidationMessage(err?.errors) ||
                    err?.message ||
                    e.message ||
                    'Falha ao guardar integração.';
                this.$toast.add({ severity: 'error', summary: 'Erro', detail: msg, life: 6000 });
            } finally {
                this.integracaoSaving = false;
            }
        },
        confirmSaveLegacy() {
            const trimmed = (this.apiKey || '').trim();
            if (!trimmed) {
                this.$toast.add({
                    severity: 'warn',
                    summary: 'Atenção',
                    detail: 'Digite a chave API antes de guardar.',
                    life: 4000,
                });
                return;
            }
            this.$confirm.require({
                message:
                    'Você está trocando a chave legado. Tem certeza de que deseja fazer isso? Se a chave estiver incorreta, o upload por IA pode deixar de funcionar.',
                header: 'Confirmar alteração da chave',
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: 'Sim',
                rejectLabel: 'Não',
                acceptClass: 'bg-red-500 text-white border-red-500',
                rejectClass: 'p-button-secondary',
                accept: () => {
                    this.save();
                },
            });
        },
        confirmRemoveLegacyOverride() {
            this.$confirm.require({
                message:
                    'Você vai remover a chave legado guardada na base. Tem certeza? Sem essa chave, só contam o GEMINI_API_KEY do ambiente ou as integrações da tabela abaixo — o upload por IA pode deixar de funcionar se nada estiver válido.',
                header: 'Confirmar remoção da chave',
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: 'Sim',
                rejectLabel: 'Não',
                acceptClass: 'bg-red-500 text-white border-red-500',
                rejectClass: 'p-button-secondary',
                accept: () => {
                    this.removeOverride();
                },
            });
        },
        confirmRemoveIntegracao(row) {
            this.$confirm.require({
                message: 'Remover esta integração? O upload IA deixará de usar esta chave se não houver outra ativa.',
                header: 'Excluir integração',
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: 'Excluir',
                acceptClass: 'bg-red-500 text-white border-red-500',
                rejectLabel: 'Cancelar',
                accept: async () => {
                    try {
                        await ConfiguracaoSistema.iaIntegracaoDestroy(row.id);
                        this.$toast.add({
                            severity: 'success',
                            summary: 'Sucesso',
                            detail: 'Integração removida.',
                            life: 4000,
                        });
                        await Promise.all([this.loadIntegracoes(), this.loadStatus()]);
                    } catch (e) {
                        const msg = e.response?.data?.message || e.message || 'Falha ao remover.';
                        this.$toast.add({ severity: 'error', summary: 'Erro', detail: msg, life: 5000 });
                    }
                },
            });
        },
        async save() {
            const trimmed = (this.apiKey || '').trim();
            if (!trimmed) {
                this.$toast.add({
                    severity: 'warn',
                    summary: 'Atenção',
                    detail: 'Digite a chave API antes de guardar.',
                    life: 4000,
                });
                return;
            }
            this.saving = true;
            try {
                const { data } = await ConfiguracaoSistema.geminiUpdate({ api_key: trimmed });
                this.applyResponsePayload(data);
                this.apiKey = '';
                this.$toast.add({
                    severity: 'error',
                    summary: 'Chave legado',
                    detail: 'Chave legado trocada.',
                    life: 8000,
                });
            } catch (e) {
                const msg =
                    e.response?.data?.errors?.api_key?.[0] ||
                    e.response?.data?.message ||
                    e.message ||
                    'Falha ao guardar.';
                this.$toast.add({ severity: 'error', summary: 'Erro', detail: msg, life: 5000 });
            } finally {
                this.saving = false;
            }
        },
        async removeOverride() {
            this.saving = true;
            try {
                const { data } = await ConfiguracaoSistema.geminiUpdate({ remove_override: true });
                this.applyResponsePayload(data);
                this.apiKey = '';
                this.$toast.add({
                    severity: 'error',
                    summary: 'Chave legado',
                    detail: 'Chave legado removida da base.',
                    life: 8000,
                });
            } catch (e) {
                const msg = e.response?.data?.message || e.message || 'Falha ao remover.';
                this.$toast.add({ severity: 'error', summary: 'Erro', detail: msg, life: 5000 });
            } finally {
                this.saving = false;
            }
        },
        applyResponsePayload(data) {
            if (!data) {
                return;
            }
            this.status = {
                database_override: !!data.database_override,
                environment_configured: !!data.environment_configured,
                effective_configured: !!data.effective_configured,
                fonte_efetiva_gemini: data.fonte_efetiva_gemini ?? null,
            };
        },
    },
};
</script>

<style scoped>
.dialog-integracao-ia :deep(.p-dialog-content) {
    padding-top: 0.5rem;
    padding-bottom: 0.75rem;
}
.dialog-integracao-ia :deep(.p-dialog-header) {
    padding-bottom: 0.5rem;
}
</style>
